import React, { useState } from 'react';
import { ArrowRight, Home, Users, TrendingUp, Heart, CheckCircle, Sparkles } from 'lucide-react';
import { HamburgerIcon } from '../components/HamburgerIcon';
import { MobileMenu } from '../components/MobileMenu';
import { DemoModal } from '../components/DemoModal';
import nfImage from '../images/nf.jpg';
import nfImage5 from '../images/nf5.jpg';
import nfImage3 from '../images/nf3.jpg';
import nfImage4 from '../images/nf4.jpg';
import nfHero from '../images/nfhero.jpg';
import scrollLogo from '../images/scroll-logo.png';
import bigLogo from '../images/big-logo.png';

const LandingPageB = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll event
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = (e) => {
    if (
      !e.target.closest('#mobile-menu-button') &&
      !e.target.closest('.demo-modal-content') &&
      !e.target.id.includes('hamburger')
    ) {
      e.preventDefault();
      setModalOpen(true);
    }
  };

  const programs = [
    {
      icon: Home,
      title: 'Housing Development',
      description: 'Quality housing solutions that strengthen community foundations and provide safe, sustainable living spaces.',
    },
    {
      icon: TrendingUp,
      title: 'Economic Empowerment',
      description: 'Programs designed to boost local economy through intelligent resource management and community support.',
    },
    {
      icon: Users,
      title: 'Community Support',
      description: 'Comprehensive services meeting human needs while fostering vibrant, thriving neighborhoods.',
    },
  ];

  const stats = [
    { value: '500+', label: 'Families Served' },
    { value: '15+', label: 'Years of Impact' },
    { value: '50+', label: 'Community Programs' },
  ];

  const impacts = [
    'Sustainable housing for hundreds of families',
    'Economic development programs creating opportunities',
    'Environmental protection through intelligent energy use',
    'Comprehensive community support services',
  ];

  return (
    <div className="min-h-screen bg-white" onClick={handleLinkClick}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md transition-all duration-300 ${
        scrolled ? 'shadow-lg' : 'shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-all duration-300 ${
            scrolled ? 'h-16' : 'h-20'
          }`}>
            <img
              src={scrolled 
                ? scrollLogo
                : bigLogo
              }
              alt="Northfield Community LDC"
              className={`transition-all duration-300 w-auto ${
                scrolled ? 'h-10' : 'h-16'
              }`}
            />
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-[#231F20] hover:text-[#5FC5B8] font-medium transition-colors duration-200">
                About
              </a>
              <a href="#programs" className="text-[#231F20] hover:text-[#5FC5B8] font-medium transition-colors duration-200">
                Programs
              </a>
              <a href="#impact" className="text-[#231F20] hover:text-[#5FC5B8] font-medium transition-colors duration-200">
                Impact
              </a>
              <a href="#contact" className="text-[#231F20] hover:text-[#5FC5B8] font-medium transition-colors duration-200">
                Contact
              </a>
              <button className="px-6 py-2.5 bg-[#5FC5B8] hover:bg-[#4db5a7] text-white font-semibold rounded-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
                Get Involved
              </button>
            </nav>

            <div className="md:hidden text-[#231F20]">
              <HamburgerIcon
                isOpen={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              />
            </div>
          </div>
        </div>
      </header>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Hero Section - Modern & Bold */}
      <section className="pt-32 pb-24 px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5FC5B8]/5 via-transparent to-[#231F20]/5"></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#5FC5B8]/10 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-[#5FC5B8]" />
                <span className="font-jakarta text-sm font-semibold text-[#5FC5B8]">
                  Empowering Communities Since 2008
                </span>
              </div>
              <h1 className="font-jakarta text-5xl lg:text-6xl xl:text-7xl font-bold text-[#231F20] leading-[1.1] mb-6">
                Building Thriving Communities
                <span className="text-[#5FC5B8]"> Together</span>
              </h1>
              <p className="font-source text-xl text-gray-600 leading-relaxed mb-10">
                We believe that the strength of a community lies in the quality of its housing, the vibrancy of its local economy, and its provision for human needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#231F20] hover:bg-[#231F20]/90 text-white font-bold rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                  Discover Our Impact
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
                <button className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#5FC5B8] text-[#5FC5B8] hover:bg-[#5FC5B8] hover:text-white font-bold rounded-full transition-all duration-300 hover:-translate-y-1">
                  Get in Touch
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-[3rem] overflow-hidden shadow-2xl relative">
                <img
                  src={nfHero}
                  alt="Northfield community event"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#5FC5B8]/10 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Bold & Impactful */}
      <section className="py-20 bg-[#231F20] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#5FC5B8] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#5FC5B8] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-3 gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-jakarta text-6xl lg:text-7xl font-bold text-[#5FC5B8] mb-3">
                  {stat.value}
                </div>
                <div className="font-source text-white/90 text-xl font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section - Clean & Modern */}
      <section id="about" className="py-24 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-jakarta text-5xl lg:text-6xl font-bold text-[#231F20] mb-6">
              Our Mission
            </h2>
            <div className="w-20 h-1.5 bg-[#5FC5B8] mx-auto rounded-full mb-8"></div>
          </div>
          <p className="font-source text-2xl text-gray-600 leading-relaxed text-center">
            We strive to promote community strength directly and with the support of other resources. Through the intelligent use of energy, protection of the environment, and economic empowerment, we create lasting positive change.
          </p>
        </div>
      </section>

      {/* Community Programs Gallery Section */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <img
                src={nfImage4}
                alt="Northfield community gathering"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <img
                src={nfImage5}
                alt="Community programs in action"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <img
                src={nfImage3}
                alt="Community support services"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section - Card-Based */}
      <section id="programs" className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-jakarta text-5xl lg:text-6xl font-bold text-[#231F20] mb-6">
              What We Do
            </h2>
            <p className="font-source text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive programs designed to strengthen communities and empower individuals
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <div
                key={index}
                className="group bg-white p-10 rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#5FC5B8] to-[#4db5a7] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <program.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-jakarta text-2xl font-bold text-[#231F20] mb-4">
                  {program.title}
                </h3>
                <p className="font-source text-gray-600 leading-relaxed text-lg">
                  {program.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-jakarta text-5xl font-bold text-[#231F20] mb-8">
                Creating Lasting Impact
              </h2>
              <div className="space-y-4">
                {impacts.map((impact, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#5FC5B8] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <p className="font-source text-lg text-gray-700">{impact}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={nfImage}
                  alt="Northfield Community creating lasting impact"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Vibrant */}
      <section id="get-involved" className="py-24 px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5FC5B8] via-[#4db5a7] to-[#3da89b]"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="font-jakarta text-5xl lg:text-6xl font-bold text-white mb-8">
            Ready to Make a Difference?
          </h2>
          <p className="font-source text-2xl text-white/95 leading-relaxed mb-10">
            Join us in building stronger, more vibrant communities that serve everyone.
          </p>
          <button className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-white hover:bg-gray-50 text-[#231F20] font-bold text-lg rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
            Get Involved Today
            <Heart className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
          </button>
        </div>
      </section>

      {/* Accessibility Section */}
      <section className="py-20 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h3 className="font-jakarta text-3xl font-bold text-[#231F20] mb-10 text-center">
            Accessibility & Inclusive Services
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm">
              <CheckCircle className="w-6 h-6 text-[#5FC5B8] flex-shrink-0 mt-1" />
              <p className="font-source text-gray-700">
                Reserved parking spaces and wheelchair accessible ramp
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm">
              <CheckCircle className="w-6 h-6 text-[#5FC5B8] flex-shrink-0 mt-1" />
              <p className="font-source text-gray-700">
                Bi-lingual counselors available on staff
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm">
              <CheckCircle className="w-6 h-6 text-[#5FC5B8] flex-shrink-0 mt-1" />
              <p className="font-source text-gray-700">
                Video Relay Service (VRS) for deaf, hard of hearing, or speech-impaired
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm">
              <CheckCircle className="w-6 h-6 text-[#5FC5B8] flex-shrink-0 mt-1" />
              <p className="font-source text-gray-700">
                Full accessibility accommodations available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#231F20] text-white py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="inline-block bg-white px-4 py-2 rounded-lg mb-6">
                <img
                  src={bigLogo}
                  alt="Northfield Community LDC"
                  className="h-12 w-auto"
                />
              </div>
              <p className="font-source text-gray-300 text-base leading-relaxed">
                Building thriving communities together through sustainable development and empowerment.
              </p>
            </div>
            <div>
              <h4 className="font-jakarta text-xl font-bold mb-6">Quick Links</h4>
              <ul className="space-y-3 font-source">
                <li>
                  <a href="#about" className="text-gray-300 hover:text-[#5FC5B8] transition-colors duration-200 text-base">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#programs" className="text-gray-300 hover:text-[#5FC5B8] transition-colors duration-200 text-base">
                    Our Programs
                  </a>
                </li>
                <li>
                  <a href="#impact" className="text-gray-300 hover:text-[#5FC5B8] transition-colors duration-200 text-base">
                    Our Impact
                  </a>
                </li>
                <li>
                  <a href="#get-involved" className="text-gray-300 hover:text-[#5FC5B8] transition-colors duration-200 text-base">
                    Get Involved
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-jakarta text-xl font-bold mb-6">Contact Us</h4>
              <div className="space-y-3 font-source">
                <p className="text-gray-300 text-base">
                  Phone: <span className="text-white">212-NEW-YORK</span>
                </p>
                <p className="text-gray-300 text-base">
                  (212-639-9675)
                </p>
                <p className="text-gray-300 text-base">
                  Accessible: <span className="text-white">212-504-4115</span>
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="font-source text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} Northfield Community LDC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <DemoModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default LandingPageB;